import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useDebounce } from 'api/cutsom-hooks';
import { useEffect, useState } from 'react';
import { v4 as UIDV4 } from 'uuid';
import { format } from 'date-fns';
import { openSnackbar } from 'api/snackbar';
import { calculateSellingPriceWithFallback } from 'utils/apiHelper';
import { getBatchWeightedSellingPrice } from 'utils/batchAllocation';
import { getSelectedStore } from 'utils/localStorage';
import { getItemGstPercentage } from 'utils/gst';
import { formatPrice } from 'pages/invoice/utils';

/**
 * Lab / pharmacy line amount: free typing with local buffer; Formik errors show after submit/touch.
 */
function LabPharmacyAmountField({
  name,
  id,
  label,
  itemData,
  calculatedAmount,
  onEditItem,
  onBlur,
  readOnly,
  error
}) {
  const [textBuffer, setTextBuffer] = useState(null);

  const rowIdentity = `${itemData?.labTestId || ''}-${itemData?.medicineId || ''}-${itemData?.nonMedicineId || ''}`;

  useEffect(() => {
    setTextBuffer(null);
  }, [rowIdentity]);

  const defaultFormatted = calculatedAmount.toFixed(2);

  const committedDisplayString = () => {
    const a = itemData?.amount;
    if (a === '' || a === undefined || a === null) return defaultFormatted;
    const n = Number(a);
    if (Number.isNaN(n)) return defaultFormatted;
    if (n === 0) return defaultFormatted;
    if (Math.abs(n - calculatedAmount) < 0.005) return defaultFormatted;
    return n.toFixed(2);
  };

  const displayValue = textBuffer !== null ? textBuffer : committedDisplayString();

  return (
    <TableCell sx={{ padding: '8px 4px' }}>
      <TextField
        fullWidth
        type="text"
        inputMode="decimal"
        name={name}
        id={id}
        label={label}
        value={displayValue}
        onFocus={() => {
          setTextBuffer(committedDisplayString());
        }}
        onChange={(e) => {
          const v = e.target.value;
          setTextBuffer(v);
          if (v === '' || v === '.' || v === '-' || v === '-.') {
            onEditItem({ target: { name, value: '' } });
            return;
          }
          const n = parseFloat(v);
          if (!Number.isNaN(n)) {
            onEditItem({ target: { name, value: n } });
          }
        }}
        onBlur={(e) => {
          const raw = (textBuffer !== null ? textBuffer : e.target.value).trim();
          setTextBuffer(null);
          const n = parseFloat(raw);
          if (raw === '' || Number.isNaN(n)) {
            onEditItem({ target: { name, value: calculatedAmount } });
          } else {
            onEditItem({ target: { name, value: n } });
          }
          if (onBlur) onBlur(e);
        }}
        InputProps={{ readOnly }}
        error={Boolean(error)}
        helperText={error || ''}
      />
    </TableCell>
  );
}

LabPharmacyAmountField.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  itemData: PropTypes.object.isRequired,
  calculatedAmount: PropTypes.number.isRequired,
  onEditItem: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  readOnly: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

export default function InvoiceField({ onEditItem, onBlur, cellData, itemData, index, patientSelected,  wholesalerSelected, push, variant, isCancellationMode = false, isEditMode = false, allInvoiceItems = [], salePriceType, requestFocus, focusFieldType, userDiscount = null, hasUserDiscount = false }) {
  const { type, key, name, placeholder, id, errors, touched, label, disabled, readOnly, fetchOptions } = cellData;

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState(itemData?.name || '');
  const [isSearching, setIsSearching] = useState(false);
  const [searchAbortController, setSearchAbortController] = useState(null);
  const [saleNumericText, setSaleNumericText] = useState(null);
  
  // Increased debounce delay for better user experience and reduced API calls
  const debounced = useDebounce(inputValue, 500, 2); // 500ms delay, minimum 2 characters
  
  // Debounced quantity for price updates
  const debouncedQuantity = useDebounce(String(itemData?.qty || 0), 800);
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [lastProcessedQuantity, setLastProcessedQuantity] = useState(null);
  const [lastAbortController, setLastAbortController] = useState(null);
  
  const shouldDisable = (variant === "sale") ? (!wholesalerSelected && !isCancellationMode) : (!patientSelected && !isCancellationMode);

  const getNumberInputProps = () => {
    // For discount fields, allow any step value to prevent browser validation popup
    if (name && name.includes('discount_in_perc')) {
      return { min: 0, step: 'any' };
    }
    
    // For quantity fields (qty), only allow integers
    if (name && (name.includes('.qty') || name.includes('qty'))) {
      return { min: 0, step: 1 };
    }
    
    if (variant === 'pharmacy' || variant === 'lab' || variant === 'sale') {
      return { min: 0, step: 'any' };
    } else if (variant === 'inventory') {
      return { min: 0, step: 'any' };
    } else {
      return {min: 0, step: 1 };
    }
  }

  const getErrorText = () => {
    if(variant === 'pharmacy' || variant === 'lab'){
      if (name.includes('amount')) {
        return cellData.errors || '';
      }

      return !patientSelected && !isCancellationMode ? "Select a patient first" : "";
    } else if (variant === 'sale') {
      return !wholesalerSelected && !isCancellationMode ? "Select a wholesaler first" : "";
    } else {
      return errors && touched ? errors : "";
    }
  }

  // Function to check if medicine is already in the invoice
  const checkForDuplicateMedicine = (newItemId, currentIndex) => {
    if (isCancellationMode) return false; // Skip check in cancellation mode
    
    return allInvoiceItems.some((item, itemIndex) => {
      if (itemIndex === currentIndex) return false; // Skip current item
      
      const existingId = (variant === "lab" || variant === "lab_bulk_cancel") 
        ? item.labTestId 
        : (item.medicineId || item.nonMedicineId);
        
      return Boolean(existingId) && String(existingId) === String(newItemId);
    });
  };

  const getSubtotalOfSaleItem = (itemData) => {
    const batchAllocations = itemData?.batch_allocations;
    if (!batchAllocations || batchAllocations?.hasInsufficientStock) {
      return 0;
    }
    if (salePriceType === "selling_price") {
      return batchAllocations?.allocations?.reduce((total, allocation) => 
        total + parseFloat(allocation.sellingPrice || itemData?.selling_price || 0) * allocation.quantity,
        0);
    } else {
      return batchAllocations?.allocations?.reduce((total, allocation) => 
        total + parseFloat(allocation.unitCost || itemData?.buying_price || 0) * allocation.quantity,
        0);
    }
  }   

  useEffect(() => {
    let active = true;
    
    // Cancel any previous search request
    if (searchAbortController) {
      searchAbortController.abort();
    }
    
    if (type === 'autocomplete' && typeof fetchOptions === 'function') {
      if (debounced.length < 2) {
        setOptions(prev => {
          const sel = prev.find(opt => opt.id === itemData?.labTestId || opt.id === itemData?.medicineId);
          return sel ? [sel] : [];
        });
        setIsSearching(false);
      } else {
        setIsSearching(true);
        
        // Create new abort controller for this request
        const abortController = new AbortController();
        setSearchAbortController(abortController);
        
        fetchOptions(debounced)
          .then(data => {
            if (!active || abortController.signal.aborted) return;
            
            let newOpts
            if(variant === "pharmacy" || variant === "sale" || variant === "pharmacy_bulk_cancel"){
              newOpts = data || []
              // Filter out disabled medicines - only show active medicines to avoid confusion
              newOpts = newOpts.filter(item => {
                // For medicines, check is_active field; for non-medical items, always include
                if (item.type === "non-medical") return true;
                return item.is_active !== false; // Include if is_active is true or undefined (backward compatibility)
              });
              newOpts = newOpts.map((item)=>{
                return {...item,name: item.type === "non-medical" ? item.name : `${item.product_name || item.brand_name} (${item.constituents_names.join(' , ')})`}})
            }else{
              newOpts = data.results || [];
            }
            const selId = itemData?.labTestId || itemData?.medicineId;
            if (selId && !newOpts.some(o => o.id === selId)) {
              setOptions(currentOptions => {
                const existing = currentOptions.find(o => o.id === selId);
                if (existing) newOpts.push(existing);
                else if (itemData?.name) {
                  // Use selling_price for lab tests, price for others
                  const price = (variant === "lab" || variant === "lab_bulk_cancel") 
                    ? (itemData.selling_price || itemData.price || 0)
                    : (itemData.price || itemData.selling_price || 0);
                  newOpts.push({ id: selId, name: itemData.name, price: price });
                }
                return newOpts;
              });
            } else {
              setOptions(newOpts);
            }
            setIsSearching(false);
          })
          .catch((err) =>{
            if (active && !abortController.signal.aborted) {
              setOptions([]);
              setIsSearching(false);
            }
          });
      }
    }
    
    return () => { 
      active = false;
      if (searchAbortController) {
        searchAbortController.abort();
      }
    };
  }, [debounced, type, fetchOptions, itemData, variant]);

  useEffect(() => {
    if (variant === "pharmacy" || variant === "sale") {
      const strips = parseInt((itemData?.qty) / (itemData?.packaging_size || 1));
      const pcs = (itemData?.qty) % (itemData?.packaging_size || 1);
      onEditItem({ target: { name: `invoice_detail[${index}].strips`, value: strips } });
      onEditItem({ target: { name: `invoice_detail[${index}].pcs`, value: pcs } });
    }
  },[ variant, index, itemData?.medicineId, itemData?.qty, itemData?.packaging_size, onEditItem]);

  // Keep the displayed unit price aligned with the batches allocated to the line.
  useEffect(() => {
    if ((variant === "sale" || variant === "pharmacy") && (itemData?.medicineId || itemData?.nonMedicineId)) {
      const quantityNum = Number(itemData?.qty || 0);
      const currentPrice = Number(itemData?.selling_price || 0);
      
      // If quantity is valid and batch allocations exist, calculate price from allocations
      if (quantityNum > 0 && itemData?.batch_allocations?.allocations?.length > 0) {
        const allocations = itemData.batch_allocations.allocations;
        const totalQuantity = allocations.reduce((sum, alloc) => sum + Number(alloc.quantity || 0), 0);
        
        if (totalQuantity > 0) {
          let averagePrice = 0;
          
          if (variant === "pharmacy" || salePriceType === "selling_price") {
            // Selected inventory batches are the source of truth for pharmacy
            // pricing; catalog prices must not overwrite this value.
            averagePrice = getBatchWeightedSellingPrice(allocations);
          } else {
            const totalPrice = allocations.reduce((sum, alloc) => 
              sum + (parseFloat(alloc.unitCost || 0) * Number(alloc.quantity || 0)), 0);
            averagePrice = totalPrice / totalQuantity;
          }
          
          // Update selling_price from batch allocations if it's 0 or significantly different
          if (averagePrice !== null && (currentPrice === 0 || Math.abs(averagePrice - currentPrice) > 0.0001)) {
            onEditItem({ target: { name: `invoice_detail[${index}].selling_price`, value: formatPrice(averagePrice) } });
          }
        }
      } else if (quantityNum > 0 && currentPrice === 0 && itemData?.batch_allocations) {
        // If price is 0 but we have batch allocations (might be recalculating), try to preserve from previous allocations
        // This prevents the price from being cleared during batch allocation recalculation
        const allocations = itemData.batch_allocations.allocations || [];
        if (allocations.length > 0) {
          // Use the first allocation's price as fallback
          const fallbackPrice = variant === "pharmacy" || salePriceType === "selling_price"
            ? (allocations[0]?.sellingPrice || 0)
            : (allocations[0]?.unitCost || 0);
          
          if (fallbackPrice > 0) {
            onEditItem({ target: { name: `invoice_detail[${index}].selling_price`, value: formatPrice(fallbackPrice) } });
          }
        }
      }
    }
  }, [variant, index, itemData?.qty, itemData?.batch_allocations, itemData?.selling_price, itemData?.medicineId, itemData?.nonMedicineId, salePriceType, onEditItem]);

  // Update price when quantity changes for pharmacy items (debounced to reduce API calls)
  useEffect(() => {
    // Cancel any previous request
    if (lastAbortController) {
      lastAbortController.abort();
    }
    
    const abortController = new AbortController();
    setLastAbortController(abortController);
    
    const updatePriceForQuantityChange = async () => {
      // prevents multiple concurrent calls and infinite loops
      const quantityNum = Number(debouncedQuantity);
      const allocatedQuantity = (itemData?.batch_allocations?.allocations || []).reduce(
        (total, allocation) => total + Number(allocation.quantity || 0),
        0
      );
      if (
        isUpdatingPrice || 
        variant !== "pharmacy" || 
        (!itemData?.medicineId && !itemData?.nonMedicineId) || 
        quantityNum < 0 ||
        (quantityNum > 0 && allocatedQuantity === quantityNum) ||
        quantityNum === lastProcessedQuantity ||
        abortController.signal.aborted
      ) {
        return;
      }

      setIsUpdatingPrice(true);
      setLastProcessedQuantity(quantityNum);
      
      try {
        const storeId = getSelectedStore();
        
        if (abortController.signal.aborted) {
          return;
        }

        if (quantityNum === 0) {
          // Reset price when quantity is 0 or empty
          onEditItem({ target: { name: `invoice_detail[${index}].selling_price`, value: '0' } });
          setIsUpdatingPrice(false);
          return;
        }
        
        const priceData = await calculateSellingPriceWithFallback(
          itemData?.medicineId || null,
          itemData?.nonMedicineId || null,
          quantityNum,
          storeId,
          Number(itemData?.selling_price || 0), // use current price as fallback
          abortController.signal
        );
        
        if (abortController.signal.aborted) {
          return;
        }
        
        const newPrice = priceData.finalPrice.toFixed(2);
        const currentPrice = Number(itemData?.selling_price || 0).toFixed(2);
        
        if (Math.abs(parseFloat(newPrice) - parseFloat(currentPrice)) > 0.01) {
          onEditItem({ target: { name: `invoice_detail[${index}].selling_price`, value: newPrice } });
        }
      } catch (error) {
        if (error.message === 'Request cancelled' || abortController.signal.aborted) {
          // Request was cancelled, ignore
        } else {
          console.error('Error updating price for quantity change:', error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsUpdatingPrice(false);
        }
      }
    };

    if ((itemData?.medicineId || itemData?.nonMedicineId) && Number(debouncedQuantity) >= 0 && !isEditMode) {
      updatePriceForQuantityChange();
    } else if (variant === 'lab' && itemData?.labTestId && Number(debouncedQuantity) >= 0 && !isEditMode) {
      ensureSellingPriceForQuantityChange();
    } else {
      setIsUpdatingPrice(false);
    }

    return () => {
      if (abortController && !abortController.signal.aborted) {
        abortController.abort();
      }
    };
  }, [variant, index, itemData?.medicineId, itemData?.nonMedicineId, itemData?.labTestId, itemData?.batch_allocations, debouncedQuantity, isEditMode]);

  const ensureSellingPriceForQuantityChange = () => {
    const quantityNum = Number(debouncedQuantity);
    const basePrice = Number(itemData?.base_price || itemData?.selling_price || 0);

    if (quantityNum === 0) {
      return;
    }

    if (Number(itemData?.selling_price || 0) === 0 && basePrice > 0) {
      onEditItem({
        target: { name: `invoice_detail[${index}].selling_price`, value: basePrice.toFixed(2) }
      });
    }
  };

  // Reset processed quantity when medicine changes
  useEffect(() => {
    setLastProcessedQuantity(null);
    setIsUpdatingPrice(false);
  }, [itemData?.medicineId, itemData?.nonMedicineId, itemData?.labTestId]);

  useEffect(() => {
    if (type === 'autocomplete' && itemData?.name) {
      setInputValue(itemData.name);
    } else if (type === 'autocomplete' && !itemData?.name) {
      setInputValue('');
    }
  }, [itemData?.name, type]);

  if (type === 'autocomplete') {
    const selectedObj = itemData?.labTestId || itemData?.medicineId
      ? { id: itemData.labTestId || itemData.medicineId, name: itemData?.product_name || itemData?.brand_name || itemData.name || '', price: itemData.selling_price || itemData.price || 0 }
      : null;

    const isCancelled = itemData?.isCancelled || (itemData?.qty <= 0 && isCancellationMode);
    const shouldDisable = (
      (variant === "sale") ? (!wholesalerSelected && !isCancellationMode) 
      : ((!patientSelected && !isCancellationMode) || isCancelled)
    ) || cellData.readOnly;

    return (
      <TableCell sx={{ width: '40%', padding: '8px 4px' }}>
        <Box>
          <Autocomplete
            options={options}
            filterOptions={(options) => options}
            getOptionLabel={opt => opt.name || ''}
            value={selectedObj}
            inputValue={inputValue}
            loading={isSearching}
            loadingText="Searching medicines..."
            onInputChange={(_, v, reason) => reason === 'input' && setInputValue(v)}
            onChange={async (_, newVal) => {
              if (!newVal) {
                setInputValue('');
                return;
              }
              
              // Check for duplicates before proceeding
              if (checkForDuplicateMedicine(newVal?.id, index)) {
                openSnackbar({
                  open: true,
                  message: `${(variant === "lab" || variant === "lab_bulk_cancel") ? "Test" : "Medicine"} "${newVal?.product_name || newVal?.brand_name || newVal?.name}" is already in the invoice. Please select a different item.`,
                  variant: 'alert',
                  alert: { color: 'warning' }
                });
                setInputValue('');
                return;
              }
              
              const idKey = (variant === "lab" || variant === "lab_bulk_cancel") ? 'labTestId' : (newVal.type === "non-medical" ? 'nonMedicineId' : 'medicineId');
              const nameVal = newVal?.product_name || newVal?.brand_name || newVal?.name || '';
              let priceVal = Number(newVal?.price || newVal?.avg_selling_price || 0);
              const catalogPrice = Number(newVal?.price || 0);
              
              // Try to get selling price from purchase bills with fallback
              if (variant === "pharmacy" && !(catalogPrice > 0)) {
                const storeId = getSelectedStore();
                const currentQuantity = itemData?.qty || 1;
                
                try {
                  const priceData = await calculateSellingPriceWithFallback(
                    newVal.type !== "non-medical" ? newVal?.id : null,
                    newVal.type === "non-medical" ? newVal?.id : null,
                    currentQuantity,
                    storeId,
                    priceVal // fallback to original medicine price
                  );
                  
                  priceVal = priceData.finalPrice;
                } catch (error) {
                  console.error('Error calculating selling price, using default:', error);
                }
              }
              
              onEditItem({ target: { name: `invoice_detail[${index}].${idKey}`, value: newVal?.id || '' } });
              onEditItem({ target: { name: `invoice_detail[${index}].name`, value: nameVal } });
              onEditItem({ target: { name: `invoice_detail[${index}].hsn_code`, value: newVal?.hsn_code || '' } });
              onEditItem({ target: { name: `invoice_detail[${index}].selling_price`, value: priceVal.toFixed(2) } });
              if (variant === "lab" || variant === "lab_bulk_cancel") {
                onEditItem({ target: { name: `invoice_detail[${index}].base_price`, value: priceVal.toFixed(2) } });
                onEditItem({ target: { name: `invoice_detail[${index}].type`, value: newVal?.type || 'test' } });
                onEditItem({ target: { name: `invoice_detail[${index}].tests`, value: newVal?.type === 'package' ? (newVal?.originalData?.tests || []) : [] } });
              }
              
              if (variant === "pharmacy" || variant === "sale") {
                let finalDiscount = 0;
                // Only apply medicine default discount for pharmacy invoices, NOT for sale bills or purchase bills
                if (variant === "pharmacy") {
                  const hasItemDiscount = newVal?.discount !== null && newVal?.discount !== undefined;
                  finalDiscount = hasItemDiscount ? Number(newVal.discount) : (hasUserDiscount && userDiscount ? userDiscount : 0);
                }
                
                onEditItem({
                  target: {
                    name: `invoice_detail[${index}].gst_percentage`,
                    value: getItemGstPercentage(newVal)
                  }
                });

                // For sale bills, always set discount to 0 (no default discount from medicine)
                // Medicine default discount should NOT apply in sell bills or purchase bills
                onEditItem({ target: { name: `invoice_detail[${index}].discount_in_perc`, value: finalDiscount } });
              }
              
              // Set amount to calculated value (price × quantity) for lab tests
              if (variant === "lab") {
                
                // Apply a test-specific discount only when it is a positive configured value.
                // A 0% value should still allow the user's lab discount (e.g. SD-SD 50%) to apply.
                const hasLabItemDiscount = Number.isFinite(Number(newVal?.discount)) && Number(newVal.discount) > 0;
                const finalLabDiscount = hasLabItemDiscount ? Number(newVal.discount) : (hasUserDiscount && userDiscount ? userDiscount : 0);
                onEditItem({ target: { name: `invoice_detail[${index}].discount_in_perc`, value: finalLabDiscount } });
              }
              if (variant === "pharmacy" || variant === "sale") {
                onEditItem({ target: { name: `invoice_detail[${index}].packaging_size`, value: newVal?.packaging_size || ''} });
                // Set buying price for pharmacy
                const buyingPriceVal = Number(newVal?.avg_buying_price || newVal?.buying_price || 0);
                onEditItem({ target: { name: `invoice_detail[${index}].buying_price`, value: buyingPriceVal.toFixed(2) } });
              }
              setInputValue(nameVal);
              if (newVal?.id && typeof push === 'function') {
                push({
                  id: UIDV4(),
                  [idKey]:'',
                  name: '',
                  description: '',
                  qty: 1
                });
              }
              if (requestFocus) {
                requestAnimationFrame(() => requestFocus(`table-${index}-quantity`, { suppressAutoRowFocus: true }));
              }
            }}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={placeholder}
                label={label}
                error={Boolean(errors && touched)}
                onBlur={onBlur}
                inputProps={{
                  ...params.inputProps,
                  ...(focusFieldType ? { 'data-invoice-focus-field': focusFieldType } : {})
                }}
                helperText={
                  (variant === 'sale') ? getErrorText() :
                  isCancelled 
                    ? "Cancelled"
                    : !patientSelected && !isCancellationMode 
                      ? "Select a patient first" 
                      : inputValue.length > 0 && inputValue.length < 2 
                        ? "Type at least 2 characters to search"
                        : ""
                }
                sx={{
                  '& .MuiInputBase-root': {
                    ...(isCancelled && {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      color: 'text.secondary',
                      textDecoration: 'line-through',
                    }),
                  },
                  '& .MuiInputBase-input': {
                    ...(isCancelled && {
                      color: 'text.secondary',
                      textDecoration: 'line-through',
                    }),
                  },
                }}
              />
            )}
            disabled={shouldDisable || cellData.readOnly}
          />
          {cellData.customPlaceholderInfo && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5, 
              mt: 0.5,
              px: 1,
              py: 0.25,
              bgcolor: 'warning.lighter', 
              borderRadius: 0.5,
              border: '1px dashed',
              borderColor: 'warning.main'
            }}>
              <Typography variant="caption" color="warning.dark" fontWeight="medium">
                Prescribed: {cellData.customPlaceholderInfo.name}
              </Typography>
              {cellData.customPlaceholderInfo.dosage && (
                <Typography variant="caption" color="text.secondary">
                  ({cellData.customPlaceholderInfo.dosage})
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </TableCell>
    );
  }
  if (type === 'amount') {
    const originalPrice = Number(itemData.selling_price || itemData.price || 0);
    const qty = Number(itemData.qty || 1);
    const discount = Number(itemData.discount_in_perc || 0);
    const subtotal = originalPrice * qty;
    const calculatedAmount = subtotal * (1 - discount / 100);
    const manualAmount = Number(itemData.amount || 0);

    if (variant === 'sale') {
      const amount = getSubtotalOfSaleItem(itemData);
      return (
        <TableCell sx={{ padding: '8px 4px' }}>
          <TextField
            fullWidth
            type="text"
            name={name}
            id={id}
            value={amount.toFixed(2)}
            onChange={(e) => {
              onEditItem(e);
            }}
            InputProps={{ readOnly: cellData.readOnly }}
            helperText={getErrorText()}
          />
        </TableCell>
      );
    }

    if (cellData.readOnly) {
      const displayAmount = (manualAmount > 0 && manualAmount > calculatedAmount) ? manualAmount : calculatedAmount;
      return (
        <TableCell sx={{ padding: '8px 4px' }}>
          <TextField
            fullWidth
            type="number"
            name={name}
            id={id}
            label={label}
            value={displayAmount.toFixed(2)}
            InputProps={{ readOnly: true }}
            helperText={getErrorText()}
          />
        </TableCell>
      );
    }

    return (
      <LabPharmacyAmountField
        name={name}
        id={id}
        label={label}
        itemData={itemData}
        calculatedAmount={calculatedAmount}
        onEditItem={onEditItem}
        onBlur={onBlur}
        readOnly={cellData.readOnly}
        error={errors}
      />
    );
  }
  // Check if this item is cancelled (needed for editable sale bill fields)
  const isCancelled = itemData?.isCancelled || (itemData?.qty <= 0 && isCancellationMode);

  // Set default value for number fields - ensure 0 is displayed correctly
  const getDefaultValue = () => {
    if (cellData.type === 'number') {
      // For number fields, if value is undefined or null, return empty string
      // If value is 0, return 0 (not empty string)
      if (cellData.value === undefined || cellData.value === null) {
        return '';
      }
      return cellData.value;
    }
    // For non-number fields, return value or empty string
    return cellData.value !== undefined && cellData.value !== null ? cellData.value : '';
  };

  const displayValue = getDefaultValue();
  const isSaleNumericField = variant === 'sale' && cellData.type === 'number';
  const isQuantityField = Boolean(name && (name.includes('.qty') || name.includes('qty')));
  const isPercentageField = key === 'discount_in_perc' || key === 'gst_percentage' || key === 'tax_in_perc';
  const saleDisplayValue = isSaleNumericField && saleNumericText !== null ? saleNumericText : displayValue;

  const handleSaleNumericChange = (e) => {
    const inputValue = e.target.value;
    const pattern = isQuantityField ? /^\d*$/ : /^\d*(\.\d*)?$/;

    if (!pattern.test(inputValue)) return;

    setSaleNumericText(inputValue);
    if (inputValue === '' || inputValue.endsWith('.')) return;

    let numericValue = Number(inputValue);
    if (isPercentageField) {
      numericValue = Math.max(0, Math.min(100, numericValue));
    }
    onEditItem({ target: { name, value: numericValue } });
  };

  const handleSaleNumericBlur = (e) => {
    const rawValue = saleNumericText !== null ? saleNumericText : String(displayValue ?? '');
    let numericValue = rawValue === '' || rawValue === '.' ? 0 : Number(rawValue);

    if (isPercentageField) {
      numericValue = Math.max(0, Math.min(100, numericValue));
    }
    onEditItem({ target: { name, value: numericValue } });
    setSaleNumericText(null);
    if (onBlur) onBlur(e);
  };

  // Handle onChange with integer validation for quantity fields
  const handleQuantityChange = (e) => {
    if (isQuantityField && cellData.type === 'number') {
      const inputValue = e.target.value;
      
      // Allow empty string (user might be clearing the field)
      if (inputValue === '' || inputValue === null || inputValue === undefined) {
        onEditItem(e);
        return;
      }
      
      // Check if input contains a decimal point or is not a valid integer
      if (inputValue.includes('.') || inputValue.includes(',')) {
        // Remove decimal part and keep only integer part
        const integerPart = inputValue.split(/[.,]/)[0];
        if (integerPart === '' || integerPart === '-') {
          // If only decimal point or negative sign, set to empty
          e.target.value = '';
          onEditItem(e);
          return;
        }
        const intValue = parseInt(integerPart, 10);
        if (!isNaN(intValue) && intValue >= 0) {
          e.target.value = intValue;
          onEditItem(e);
        }
        return;
      }
      
      // Check if the value is a valid integer
      const numericValue = Number(inputValue);
      
      // If it's not a valid number, reject it
      if (isNaN(numericValue)) {
        return; // Ignore invalid input
      }
      
      // Ensure it's an integer and non-negative
      if (!Number.isInteger(numericValue)) {
        const roundedValue = Math.round(numericValue);
        if (!isNaN(roundedValue) && roundedValue >= 0) {
          e.target.value = roundedValue;
          onEditItem(e);
        }
        return;
      }
      
      // Ensure non-negative integers
      if (numericValue < 0) {
        e.target.value = 0;
        onEditItem(e);
        return;
      }
    }
    
    // For non-quantity fields, use the original handler
    onEditItem(e);
  };

  if (cellData.readOnly && cellData.type === 'number') {
    // For sale bills, enable selling_price field when there are no batch allocations (manual entry)
    let isDisabled = disabled || shouldDisable;
    let isReadOnly = cellData.readOnly;
    
    if (variant === "sale" && name.includes('selling_price')) {
      // Enable if no batch allocations exist (allows manual entry)
      const hasBatchAllocations = itemData?.batch_allocations?.allocations?.length > 0;
      const hasItemId = itemData?.medicineId || itemData?.nonMedicineId;
      // Disable and read-only only if we have batch allocations AND an item ID (calculated from batches)
      // Enable and editable if no batch allocations (manual entry) or no item ID (manual item)
      if (!hasBatchAllocations || !hasItemId) {
        isDisabled = false;
        isReadOnly = false;
      } else {
        isDisabled = true;
        isReadOnly = true;
      }
    }
    
    // If field should be editable, render as editable field
    if (!isReadOnly) {
      return (
        <TableCell sx={{ padding: '8px 4px' }}>
          <TextField
            fullWidth
            type={isSaleNumericField ? 'text' : 'number'}
            inputMode={isQuantityField ? 'numeric' : 'decimal'}
            placeholder={displayValue === '' ? placeholder : ''}
            name={name}
            id={id}
            value={saleDisplayValue}
            onFocus={() => isSaleNumericField && setSaleNumericText(String(displayValue ?? ''))}
            onChange={isSaleNumericField ? handleSaleNumericChange : handleQuantityChange}
            onBlur={isSaleNumericField ? handleSaleNumericBlur : onBlur}
            disabled={isDisabled || isCancelled}
            error={Boolean(errors && touched)}
            inputProps={{
              ...(cellData.type === 'number' ? getNumberInputProps() : {}),
              ...(focusFieldType ? { 'data-invoice-focus-field': focusFieldType } : {})
            }}
            helperText={isCancelled ? 'Cancelled' : getErrorText()}
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                padding: '4px 8px',
              },
              '& .MuiInputBase-input': {
                padding: '4px 8px',
              },
            }}
          />
        </TableCell>
      );
    }
    
    // Otherwise render as read-only field
    return (
      <TableCell sx={{ padding: '8px 4px' }}>
        <TextField
          fullWidth
          type={variant === 'sale' ? 'text' : 'number'}
          placeholder={cellData.value ? '' : placeholder}
          name={name}
          id={id}
          value={cellData.showEmpty ? '' : cellData.value ?? ''}
          label={variant === 'sale' && ['strips', 'pcs', 'gst_percentage'].includes(key) ? undefined : label}
          InputProps={{ readOnly: true }}
          disabled={isDisabled}
          helperText={getErrorText()}
        />
      </TableCell>
    );
  }

  // Handle created_at field formatting
  if (cellData.readOnly && name.includes('created_at')) {
    if (!cellData.value) {
      return (
        <TableCell align="center" sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">-</Typography>
        </TableCell>
      );
    }

    const date = new Date(cellData.value);
    const formattedDate = format(date, 'dd MMM yyyy');
    const formattedTime = format(date, 'HH:mm');
    
    return (
      <TableCell align="center" sx={{ py: 2, minWidth: 120 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {formattedDate}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
            {formattedTime}
          </Typography>
        </Box>
      </TableCell>
    );
  }

  // Handle created_by field
  if (cellData.readOnly && name.includes('created_by')) {
    return (
      <TableCell align="center" sx={{ py: 2, minWidth: 100 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            color: cellData.value ? 'text.primary' : 'text.secondary' 
          }}
        >
          {cellData.value || '-'}
        </Typography>
      </TableCell>
    );
  }

  return (
    <TableCell sx={{ padding: '8px 4px' }}>
      <TextField
        fullWidth
        type={isSaleNumericField ? 'text' : cellData.type}
        inputMode={isSaleNumericField ? (isQuantityField ? 'numeric' : 'decimal') : undefined}
        placeholder={displayValue === '' ? placeholder : ''}
        name={name}
        id={id}
        value={saleDisplayValue}
        onFocus={() => isSaleNumericField && setSaleNumericText(String(displayValue ?? ''))}
        onChange={isSaleNumericField ? handleSaleNumericChange : (e) => {
          let value = e.target.value;

          if (isPercentageField) {
            value = Math.max(0, Math.min(100, Number(value)));
            e.target.value = value;
          }

          handleQuantityChange(e);
        }}
        onBlur={isSaleNumericField ? handleSaleNumericBlur : onBlur}
        disabled={disabled || isCancelled || readOnly}
        error={Boolean(errors && touched)}
        inputProps={{
          ...(cellData.type === 'number' ? getNumberInputProps() : {}),
          ...(focusFieldType ? { 'data-invoice-focus-field': focusFieldType } : {}),
          ...(isPercentageField ? { min: 0, max: 100, step: 'any' } : {}),
        }}
        helperText={isCancelled ? 'Cancelled' : getErrorText()}
        size="small"
        sx={{
          '& .MuiInputBase-root': {
            fontSize: '0.875rem',
            padding: '4px 8px',
            ...(isCancelled && {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: 'text.secondary',
              textDecoration: 'line-through',
            }),
          },
          '& .MuiInputBase-input': {
            padding: '4px 8px',
            ...(isCancelled && {
              color: 'text.secondary',
              textDecoration: 'line-through',
            }),
          },
          '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        }}
      />
    </TableCell>
  );
}

InvoiceField.propTypes = {
  onEditItem: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  cellData: PropTypes.object.isRequired,
  itemData: PropTypes.object,
  index: PropTypes.number.isRequired,
  push: PropTypes.func,
  patientSelected: PropTypes.bool,
  wholesalerSelected: PropTypes.bool,
  variant: PropTypes.string,
  isCancellationMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
  allInvoiceItems: PropTypes.array,
  salePriceType: PropTypes.string,
  requestFocus: PropTypes.func,
  focusFieldType: PropTypes.string,
  userDiscount: PropTypes.number,
  hasUserDiscount: PropTypes.bool
};
